import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, Save, X, BookOpen, Users, BarChart3 } from 'lucide-react';
import api from '../utils/api';

const SubjectConfiguration = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    papers: [],
    grade_boundaries: {
      "A*": 90, "A": 80, "B": 70, "C": 60, "D": 50, "E": 40, "U": 0
    },
    core_max_grade: 'C'
  });

  const fetchSubjects = useCallback(async () => {
    try {
      const response = await api.get('/igcse/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const addPaper = () => {
    const paperNumbers = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'];
    const usedNumbers = formData.papers.map(p => p.paper_number);
    const nextNumber = paperNumbers.find(p => !usedNumbers.includes(p)) || `P${formData.papers.length + 1}`;
    
    setFormData({
      ...formData,
      papers: [...formData.papers, {
        paper_number: nextNumber,
        paper_name: '',
        paper_type: 'structured',
        max_marks: 40,
        weightage: 0,
        ao1_percentage: 0,
        ao2_percentage: 0,
        ao3_percentage: 0,
        is_core: false,
        is_extended: true
      }]
    });
  };

  const removePaper = (index) => {
    setFormData({
      ...formData,
      papers: formData.papers.filter((_, i) => i !== index)
    });
  };

  const updatePaper = (index, field, value) => {
    const updatedPapers = [...formData.papers];
    updatedPapers[index] = { ...updatedPapers[index], [field]: value };
    setFormData({ ...formData, papers: updatedPapers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubject) {
        await api.put(`/igcse/subjects/${editingSubject.id}`, formData);
      } else {
        await api.post('/igcse/subjects', formData);
      }
      fetchSubjects();
      resetForm();
    } catch (error) {
      console.error('Error saving subject:', error);
      alert(error.response?.data?.detail || 'Error saving subject');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingSubject(null);
    setFormData({
      name: '',
      code: '',
      papers: [],
      grade_boundaries: {
        "A*": 90, "A": 80, "B": 70, "C": 60, "D": 50, "E": 40, "U": 0
      },
      core_max_grade: 'C'
    });
  };

  const editSubject = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name,
      code: subject.code,
      papers: subject.papers || [],
      grade_boundaries: subject.grade_boundaries || { "A*": 90, "A": 80, "B": 70, "C": 60, "D": 50, "E": 40, "U": 0 },
      core_max_grade: subject.core_max_grade || 'C'
    });
    setShowForm(true);
  };

  const deleteSubject = async (subjectId) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;
    try {
      await api.delete(`/igcse/subjects/${subjectId}`);
      fetchSubjects();
    } catch (error) {
      console.error('Error deleting subject:', error);
    }
  };

  // Calculate total weightage for validation
  const totalCoreWeightage = formData.papers.filter(p => p.is_core).reduce((sum, p) => sum + (parseFloat(p.weightage) || 0), 0);
  const totalExtendedWeightage = formData.papers.filter(p => p.is_extended).reduce((sum, p) => sum + (parseFloat(p.weightage) || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">IGCSE Subject Configuration</h1>
                <p className="text-sm text-gray-500">Configure subjects with papers, weightages, and AO distributions</p>
              </div>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="add-subject-btn"
            >
              <Plus className="w-5 h-5" />
              Add Subject
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border flex items-center gap-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Subjects</p>
              <p className="text-2xl font-bold text-gray-900">{subjects.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Papers</p>
              <p className="text-2xl font-bold text-gray-900">
                {subjects.reduce((sum, s) => sum + (s.papers?.length || 0), 0)}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">AO Tracking</p>
              <p className="text-2xl font-bold text-gray-900">AO1, AO2, AO3</p>
            </div>
          </div>
        </div>

        {/* Subject Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingSubject ? 'Edit Subject' : 'Add New Subject'}
                </h2>
                <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., Physics"
                      required
                      data-testid="subject-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject Code</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., PHY"
                      required
                      data-testid="subject-code-input"
                    />
                  </div>
                </div>

                {/* Core Max Grade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Core Track Maximum Grade
                    <span className="text-xs text-gray-500 ml-2">(Core A = Extended ?)</span>
                  </label>
                  <select
                    value={formData.core_max_grade}
                    onChange={(e) => setFormData({ ...formData, core_max_grade: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="C">C (Standard)</option>
                    <option value="D">D</option>
                    <option value="B">B</option>
                  </select>
                </div>

                {/* Papers Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Papers</h3>
                    <button
                      type="button"
                      onClick={addPaper}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
                      data-testid="add-paper-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Paper
                    </button>
                  </div>

                  {/* Weightage Summary */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                    <div className="flex gap-6">
                      <span className={`${totalCoreWeightage === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                        Core Weightage: <strong>{totalCoreWeightage}%</strong> {totalCoreWeightage !== 100 && '(should be 100%)'}
                      </span>
                      <span className={`${totalExtendedWeightage === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                        Extended Weightage: <strong>{totalExtendedWeightage}%</strong> {totalExtendedWeightage !== 100 && '(should be 100%)'}
                      </span>
                    </div>
                  </div>

                  {formData.papers.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                      <p className="text-gray-500">No papers added yet. Click "Add Paper" to begin.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.papers.map((paper, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Paper {paper.paper_number}</h4>
                            <button
                              type="button"
                              onClick={() => removePaper(index)}
                              className="p-1 text-red-500 hover:bg-red-100 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Paper Number</label>
                              <select
                                value={paper.paper_number}
                                onChange={(e) => updatePaper(index, 'paper_number', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                              >
                                {['P1', 'P2', 'P3', 'P4', 'P5', 'P6'].map(p => (
                                  <option key={p} value={p}>{p}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Paper Name</label>
                              <input
                                type="text"
                                value={paper.paper_name}
                                onChange={(e) => updatePaper(index, 'paper_name', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                                placeholder="MCQ / Structured"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Paper Type</label>
                              <select
                                value={paper.paper_type}
                                onChange={(e) => updatePaper(index, 'paper_type', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                              >
                                <option value="mcq">MCQ</option>
                                <option value="structured">Structured</option>
                                <option value="practical">Practical</option>
                                <option value="alternate_practical">Alternate to Practical</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Max Marks</label>
                              <input
                                type="number"
                                value={paper.max_marks}
                                onChange={(e) => updatePaper(index, 'max_marks', parseInt(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                                min="0"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Weightage %</label>
                              <input
                                type="number"
                                value={paper.weightage}
                                onChange={(e) => updatePaper(index, 'weightage', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">AO1 %</label>
                              <input
                                type="number"
                                value={paper.ao1_percentage}
                                onChange={(e) => updatePaper(index, 'ao1_percentage', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">AO2 %</label>
                              <input
                                type="number"
                                value={paper.ao2_percentage}
                                onChange={(e) => updatePaper(index, 'ao2_percentage', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">AO3 %</label>
                              <input
                                type="number"
                                value={paper.ao3_percentage}
                                onChange={(e) => updatePaper(index, 'ao3_percentage', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 text-sm border rounded"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div className="flex items-end gap-2">
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={paper.is_core}
                                  onChange={(e) => updatePaper(index, 'is_core', e.target.checked)}
                                  className="rounded"
                                />
                                Core
                              </label>
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={paper.is_extended}
                                  onChange={(e) => updatePaper(index, 'is_extended', e.target.checked)}
                                  className="rounded"
                                />
                                Extended
                              </label>
                            </div>
                          </div>

                          {/* AO Total Validation */}
                          {(paper.ao1_percentage + paper.ao2_percentage + paper.ao3_percentage) !== 100 && (
                            <p className="text-xs text-orange-600">
                              AO percentages should sum to 100% (currently: {paper.ao1_percentage + paper.ao2_percentage + paper.ao3_percentage}%)
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    data-testid="save-subject-btn"
                  >
                    <Save className="w-4 h-4" />
                    {editingSubject ? 'Update Subject' : 'Save Subject'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Subjects List */}
        {subjects.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Subjects Configured</h3>
            <p className="text-gray-500 mb-4">Add your first IGCSE subject with paper configurations</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus className="w-5 h-5" />
              Add Subject
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subjects.map((subject) => (
              <div
                key={subject.id}
                className="bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition-shadow"
                data-testid={`subject-card-${subject.code}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{subject.name}</h3>
                    <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                      {subject.code}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => editSubject(subject)}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                      data-testid={`edit-subject-${subject.code}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteSubject(subject.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      data-testid={`delete-subject-${subject.code}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <p className="text-gray-600">
                    <span className="font-medium">{subject.papers?.length || 0}</span> Papers Configured
                  </p>
                  
                  {/* Papers Summary */}
                  <div className="flex flex-wrap gap-1">
                    {subject.papers?.map((paper, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-0.5 text-xs rounded ${
                          paper.is_extended ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {paper.paper_number} ({paper.weightage}%)
                      </span>
                    ))}
                  </div>

                  <p className="text-gray-500 text-xs">
                    Core Max: {subject.core_max_grade}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectConfiguration;
